import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProjectService } from '../services/ProjectService';
import { Loader2, Plus, Trash2, Edit2, Play, FileText } from 'lucide-react';
import { ConfirmationModal } from '../components/ConfirmationModal';
import './ProjectsDashboardPage.css';

export const ProjectsDashboardPage = ({ session }) => {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [projectToDelete, setProjectToDelete] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (session?.access_token) {
            fetchProjects();
        }
    }, [session]);

    const fetchProjects = async () => {
        setLoading(true);
        try {
            const data = await ProjectService.listProjects(session.access_token);
            setProjects(data.projects || []);
        } catch (err) {
            console.error(err);
            setError('Failed to load projects');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (e, project) => {
        e.stopPropagation();
        setProjectToDelete(project);
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!projectToDelete) return;

        try {
            await ProjectService.deleteProject(session.access_token, projectToDelete.id);
            setProjects(projects.filter(p => p.id !== projectToDelete.id));
            setIsDeleteModalOpen(false);
            setProjectToDelete(null);
        } catch (err) {
            console.error(err);
            alert('Failed to delete project');
            // Keep modal open or close it? Let's close it for now or maybe show error in modal
            // setIsDeleteModalOpen(false); 
        }
    };

    const handleOpenProject = (project) => {
        if (project.type === 'rant') {
            navigate(`/rant-creator?project_id=${project.id}`);
        } else if (project.type === 'ranking') {
            navigate(`/ranking?project_id=${project.id}`);
        } else if (project.type === 'quiz') {
            navigate(`/quiz-creator?project_id=${project.id}`);
        } else if (project.type === 'clip_intel') {
            navigate(`/smart-clips?project_id=${project.id}`);
        } else {
            console.warn("Unknown project type:", project.type);
        }
    };

    const formatDate = (isoString) => {
        return new Date(isoString).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleNewProject = () => {
        navigate('/rant-creator'); // Default to Rant Creator for now
    };

    if (loading) {
        return (
            <div className="projects-page-loading">
                <Loader2 className="spin" size={32} />
            </div>
        );
    }

    return (
        <div className="projects-dashboard">
            <div className="projects-header">
                <h1>My Projects</h1>
                <p>Manage your saved projects</p>
            </div>

            {error && <div className="error-banner">{error}</div>}

            {projects.length === 0 ? (
                <div className="empty-state">
                    <FileText size={48} opacity={0.3} />
                    <h2>No projects yet</h2>
                    <p>Start creating your first video!</p>
                    <button className="btn-secondary" onClick={handleNewProject}>
                        Create Rant Video
                    </button>
                </div>
            ) : (
                <div className="projects-grid">
                    {projects.map(project => {
                        // Determine what video to show on hover
                        const previewUrl = project.output_url
                            || project.data?.selectedGameplay?.url
                            || null;

                        return (
                            <div
                                key={project.id}
                                className="project-card"
                                onClick={() => handleOpenProject(project)}
                                onMouseEnter={(e) => {
                                    const video = e.currentTarget.querySelector('video');
                                    if (video) video.play().catch(() => { });
                                }}
                                onMouseLeave={(e) => {
                                    const video = e.currentTarget.querySelector('video');
                                    if (video) {
                                        video.pause();
                                        video.currentTime = 0;
                                    }
                                }}
                            >
                                <div className="project-thumbnail">
                                    {project.thumbnail_url && (
                                        <img src={project.thumbnail_url} alt={project.name} />
                                    )}
                                    {previewUrl && (
                                        <video
                                            src={previewUrl}
                                            muted
                                            loop
                                            playsInline
                                            preload="metadata"
                                        />
                                    )}
                                    <div className="project-play-overlay">
                                        <Play size={48} fill="currentColor" />
                                    </div>
                                    <div className="project-type-badge">{project.type}</div>
                                </div>
                                <div className="project-info">
                                    <h3>{project.name}</h3>
                                    <span className="project-date">{formatDate(project.updated_at)}</span>
                                </div>
                                <div className="project-actions">
                                    <button
                                        className="icon-btn delete-btn"
                                        onClick={(e) => handleDeleteClick(e, project)}
                                        title="Delete"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <ConfirmationModal
                isOpen={isDeleteModalOpen}
                title="Delete Project?"
                message={`Are you sure you want to delete "${projectToDelete?.name}"? This action cannot be undone.`}
                confirmText="Delete Project"
                confirmStyle="danger"
                onConfirm={handleConfirmDelete}
                onCancel={() => {
                    setIsDeleteModalOpen(false);
                    setProjectToDelete(null);
                }}
            />
        </div>
    );
};
